import { useState, useCallback, useMemo, useEffect } from 'react';
import { Col, Row, Form, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GridReadyEvent } from 'ag-grid-community';
import { DataGrid } from '../Controls/DataGrid';
import { CSSTransition } from 'react-transition-group';
import { toast } from 'react-toastify';
import BounceArrow from '../Controls/BounceArrow';
import { GridFooter } from './GridFooter';
import { ReportProps, ReportState, FilterField, BaseFilter } from './types';
import { getDynamicDateRange, QuickDateRangeDropdown } from '../QuickDateRangeDropdown';
import { SaveReportModal } from './SaveReportModal';
import { ManageSavedReportsModal } from './ManageSavedReportsModal';
import { createApiService } from '../shared/apiConfig';
import { ReportsApi, ServerTransferSavedReportResponse } from '../shared/api/axios-client';
import { LocalDate } from '@js-joda/core';
import { FloppyFill, PlayFill, X, InfoCircleFill } from 'react-bootstrap-icons';
import { getReportTypeEnum } from './utils';
import { authAtom } from '../Data/Atoms';
import { useAtom } from 'jotai';
import Select from 'react-select';

export function BaseReport<TData, TFilter extends BaseFilter>({
    facilityId,
    facilityIds,
    config,
    savedReports,
    setSavedReports,
    onFacilityIdsChange,
    onReset,
    heightOffset = 0,
    storageKey,
    valueLabelMaps
}: ReportProps<TData, TFilter> & {
    facilityIds?: number[];
    setSavedReports: React.Dispatch<
        React.SetStateAction<
            ServerTransferSavedReportResponse[]
        >
    >;
    onFacilityIdsChange?: (newFacilityIds: number[]) => void;
    onReset?: () => void;
    heightOffset?: number;
    storageKey: string;
    valueLabelMaps?: Record<string, Record<number, string>>;
}) {
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveReportName, setSaveReportName] = useState('');
    const [canSaveReport, setCanSaveReport] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);
    const [pendingOverride, setPendingOverride] = useState<TFilter | null>(null);
    const [auth] = useAtom(authAtom);

    storageKey = `${storageKey}_${auth.account?.staffId}`;

    const reportsApi = createApiService(ReportsApi);

    // Loading mask now handled globally

    // Initialize state
    const [state, setState] = useState<ReportState<TData, TFilter>>({
        data: [],
        total: 0,
        filter: { page: 1, pageSize: 25, facilityId, facilityIds } as unknown as TFilter,
        filterForm: config.defaultFilterForm,
        columnDefs: config.defaultColumns,
        columnVisibility: {},
        gridApi: null,
        showGrid: false,
        showArrow: true
    });
    const [quickDateMode, setQuickDateMode] = useState<QuickDateOption>('All');
    const [quickPayoutDateMode, setQuickPayoutDateMode] = useState<QuickDateOption>('All');

    useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    let initialVisibility: Record<string, boolean> = {};

    if (saved) {
        // load from saved
        initialVisibility = JSON.parse(saved);
    } else {
        // fallback to defaults
        config.defaultColumns.forEach(col => {
        if (col.field) {
            initialVisibility[col.field] = !col.hide;
        }
        });
    }

    const newColumnDefs = config.defaultColumns.map(col => {
        if (!col.field) return col;
        const show = initialVisibility[col.field] ?? true;
        return { ...col, hide: !show };
    });

    setState(prev => ({
        ...prev,
        columnDefs: newColumnDefs,
        columnVisibility: initialVisibility
    }));
    }, [config.defaultColumns, storageKey]);

    // Manage Reports
    useEffect(() => {
    const isAllDates =
        quickDateMode === 'All' && quickPayoutDateMode === 'All';

    const isCustom =
        quickDateMode === 'Custom' && quickPayoutDateMode === 'Custom';

    let isValidCustom = true;
    if (isCustom) {
        isValidCustom = !!(state.filterForm.from && state.filterForm.to);
    }

  const hasValidAgingCutoff =
    config.id === 'aging' &&
    state.filterForm.cutoffMode !== undefined &&
    state.filterForm.cutoffMode !== 'All' &&
    (
      state.filterForm.cutoffMode !== 'Custom' ||
      !!state.filterForm.cutoffDate
    );

    const hasValidDateRange =
        hasValidAgingCutoff ||
        (!isAllDates && isValidCustom);

    const hasFilterValues = config.filterFields.some(field => {
        if (field.type === 'date') return false;

            const value =
            state.filterForm[field.name as keyof typeof state.filterForm];

            return value !== undefined && value !== null && value !== '';
        });

    setCanSaveReport(
        (hasValidDateRange || hasFilterValues) && state.showGrid
        );
        }, [
        state.filterForm,
        state.showGrid,
        config.filterFields,
        config.id,
        quickDateMode,
        quickPayoutDateMode
    ]);

    const handleSaveReport = useCallback(async () => {
    const trimmedName = saveReportName.trim();

    if (!trimmedName) {
        toast.error('Please enter a name for the report.');
        return;
    }

    if (savedReports.some(r => r.name === trimmedName)) {
        toast.error('A report with this name already exists.');
        return;
    }

    const cleanedFilterForm = { ...state.filterForm };
    const cleanedFilter = { ...state.filter };
    if (quickDateMode === 'All') {
        delete (cleanedFilterForm as any).from;
        delete (cleanedFilterForm as any).to;
        delete (cleanedFilter as any).from;
        delete (cleanedFilter as any).to;
    }

    if (quickPayoutDateMode === 'All') {
        delete (cleanedFilterForm as any).payoutFrom;
        delete (cleanedFilterForm as any).payoutTo;
        delete (cleanedFilter as any).payoutFrom;
        delete (cleanedFilter as any).payoutTo;
    }

    (cleanedFilterForm as any).quickDateMode = quickDateMode;
    (cleanedFilterForm as any).quickPayoutDateMode = quickPayoutDateMode;

    // Include facilityIds in the filter if present
    if (facilityIds && facilityIds.length > 0) {
        (cleanedFilterForm as any).facilityIds = facilityIds;
    }

    const savePayload = {
        reportType: getReportTypeEnum(config.id),
        request: {
            facilityId: facilityId!,
            filterJson: JSON.stringify(cleanedFilterForm), 
            name: trimmedName
        }
    };
    // global mask via interceptors

    const result = await reportsApi.monitorReportsAddSavedReportPost({
        serverTransferAddSavedReportRequest: savePayload
    });
    
    if (result.status !== 200) {
        toast.error('Failed to save report.');
        return;
    }

    setSavedReports(prev => [...prev, result.data]);
   
    toast.success('Report saved successfully.');
    setSaveReportName('');
    setShowSaveModal(false);
    }, [
    saveReportName,
    savedReports,
    state.filter,
    state.filterForm,
    facilityId,
    quickDateMode,
    quickPayoutDateMode,
    config.title
    ]);

    // Handlers
    const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            filterForm: { ...prev.filterForm, [name]: value }
        }));
    }, []);

const normalizeCutoffDate = (mode?: string, custom?: string) => {
  const today = LocalDate.now();

  switch (mode) {
    case 'Today':
      return today.toString();

    case 'LastMonth':
      return today
        .withDayOfMonth(1)
        .minusDays(1)
        .toString();

    case 'Custom':
      return custom || undefined;

    default:
      return today.toString();
  }
};

const runReportWithCurrentState = useCallback(
  async (overrideFilter?: TFilter) => {
    const activeFilterForm = overrideFilter || state.filterForm;

    const cleanedFilter = { ...state.filter };
    const cleanedFilterForm = { ...activeFilterForm };

    if (quickDateMode === 'All') {
      delete (cleanedFilterForm as any).from;
      delete (cleanedFilterForm as any).to;
      delete (cleanedFilter as any).from;
      delete (cleanedFilter as any).to;
    }

    if (quickPayoutDateMode === 'All') {
      delete (cleanedFilterForm as any).payoutFrom;
      delete (cleanedFilterForm as any).payoutTo;
      delete (cleanedFilter as any).payoutFrom;
      delete (cleanedFilter as any).payoutTo;
    }

    if (config.id === 'aging') {
    const cutoffMode = (cleanedFilterForm as any).cutoffMode;

    if (cutoffMode && cutoffMode !== 'All') {
        const cutoffDate = normalizeCutoffDate(
        cutoffMode,
        (cleanedFilterForm as any).cutoffDate
        );

        (cleanedFilter as any).cutoffDate = cutoffDate;
    }

    delete (cleanedFilter as any).cutoffMode;
    delete (cleanedFilterForm as any).cutoffMode;
    delete (cleanedFilterForm as any).cutoffDate;
    }

    const filters = {
      ...cleanedFilter,
      ...cleanedFilterForm,
      facilityId,
      facilityIds,
      page: 1,
      pageSize: 1000 // Load all data
    } as TFilter;

    try {
      // global mask via interceptors
      const response = await config.getData(filters);

      if (response) {
        const items = config.transformData
          ? config.transformData(response.items)
          : response.items;

        setState(prev => ({
          ...prev,
          data: items,
          total: response.total,
          showGrid: true,
          showArrow: false
        }));
      }
    } catch (error) {
      console.error(`Error loading ${config.title.toLowerCase()} report:`, error);
    }
  },
  [state.filter, state.filterForm, facilityId, facilityIds, config, quickDateMode, quickPayoutDateMode]
);

const handleSubmit = useCallback(
  (e: React.FormEvent | null, filtersOverride?: TFilter) => {
    if (e) e.preventDefault();

    if (filtersOverride) {
      const mergedFilters = {
        ...config.defaultFilterForm,
        ...filtersOverride
      };

      const {
        quickDateMode,
        quickPayoutDateMode,
        facilityIds: overrideFacilityIds
      } = mergedFilters as any;
      
    if (quickDateMode && quickDateMode !== 'Custom' && quickDateMode !== 'All') {
        const range = getDynamicDateRange(quickDateMode);
        if (range) {
        mergedFilters.from = range.from;
        mergedFilters.to = range.to;
        }
    }

    if (quickPayoutDateMode && quickPayoutDateMode !== 'Custom' && quickPayoutDateMode !== 'All') {
        const range = getDynamicDateRange(quickPayoutDateMode);
        if (range) {
        mergedFilters.payoutFrom = range.from;
        mergedFilters.payoutTo = range.to;
        }
    }

      const normalizedFilter = {
        ...mergedFilters,
        from: mergedFilters.from,
        to: mergedFilters.to,
        payoutFrom: mergedFilters.payoutFrom,
        payoutTo: mergedFilters.payoutTo,
        quickDateMode: quickDateMode || 'All',
        quickPayoutDateMode: quickPayoutDateMode || 'All',
      };
      
      setQuickDateMode(normalizedFilter.quickDateMode);
      setQuickPayoutDateMode(normalizedFilter.quickPayoutDateMode);

      if (overrideFacilityIds && onFacilityIdsChange) {
        onFacilityIdsChange(overrideFacilityIds);
      }

      setPendingOverride(normalizedFilter);

       setTimeout(() => {
      setState(prev => ({
        ...prev,
        filterForm: { ...prev.filterForm, ...normalizedFilter },
        showGrid: false,
        showArrow: false,
      }));
      });

      return;
    }

    runReportWithCurrentState();
  },
  [runReportWithCurrentState, onFacilityIdsChange, config.defaultFilterForm]
);
    useEffect(() => {
        if (pendingOverride) {
            runReportWithCurrentState(pendingOverride);
            setPendingOverride(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.filterForm, pendingOverride]);

    const handleReset = useCallback(() => {
        setQuickDateMode('All');
        setQuickPayoutDateMode('All');
        setState(prev => ({
            ...prev,
            filterForm: config.defaultFilterForm,
            filter: { page: 1, pageSize: 25, facilityId, facilityIds } as TFilter,
            showGrid: false,
            data: [],
            showArrow: true
        }));
        // Notify parent to clear facility selection
        if (onReset) {
            onReset();
        }
    }, [facilityId, config.defaultFilterForm, onReset]);

    const toggleColumn = useCallback((field: string, show: boolean) => {
    const colDef = config.defaultColumns.find(col => col.field === field);
    if (colDef?.hide === false && !show) return;

    setState(prev => {
        const newVisibility = { ...prev.columnVisibility, [field]: show };
        localStorage.setItem(storageKey, JSON.stringify(newVisibility));

        return {
        ...prev,
        columnDefs: prev.columnDefs.map(col =>
            col.field === field ? { ...col, hide: !show } : col
        ),
        columnVisibility: newVisibility
        };
    });
    }, [config.defaultColumns, storageKey]);

    const toggleAllColumns = useCallback((show: boolean) => {
    const newVisibility: Record<string, boolean> = {};
    const newColumnDefs = config.defaultColumns.map(col => {
        if (!col.field) return col;

        if (col.hide === false) {
        newVisibility[col.field] = true;
        return { ...col, hide: false };
        }

        newVisibility[col.field] = show;
        return { ...col, hide: !show };
    });

    localStorage.setItem(storageKey, JSON.stringify(newVisibility));

    setState(prev => ({
        ...prev,
        columnDefs: newColumnDefs,
        columnVisibility: newVisibility
    }));
    }, [config.defaultColumns, storageKey]);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
        setState(prev => ({ ...prev, gridApi: params.api }));
        setTimeout(() => params.api.sizeColumnsToFit(), 100);
    }, []);

    const onRowClicked = useCallback((event: any) => {
        // Skip if clicking on pinned rows (like footer)
        if (event.rowPinned) {
            return;
        }

        const rowData = event.data;
        
        // Check if row has parkerId and facilityId
        if (rowData?.parkerId && rowData?.facilityId) {
            const url = `/facility/${rowData.facilityId}/parker/${rowData.parkerId}/overview`;
            window.open(url, '_blank');
        }
    }, []);

    // Resize handler
    useCallback(() => {
        const handleResize = () => {
            state.gridApi?.sizeColumnsToFit();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [state.gridApi]);

    // Render filter fields
    const renderFilterField = (field: FilterField) => {
        const commonProps = {
            name: field.name,
            value: state.filterForm[field.name as keyof typeof state.filterForm] || '',
            onChange: handleFilterChange,
            placeholder: field.placeholder,
            size: 'sm' as const
        };

        if (field.type === 'multiselect') {
            const raw = (state.filterForm[field.name as keyof typeof state.filterForm] as string) || '';
            const selectedValues = raw
                .split(/[,|]/)
                .map(s => s.trim())
                .filter(Boolean);
            const selectedOptions = selectedValues.map(value => {
                const opt = field.options?.find(o => o.value === value);
                return { value, label: opt?.label ?? value };
            });
            return (
                <Col md={field.colSize || 2} key={field.name}>
                    <Form.Group>
                        <Form.Label className="small">{field.label}</Form.Label>
                        <Select
                            isMulti
                            options={field.options ?? []}
                            value={selectedOptions}
                            onChange={(selected) => {
                                const value = (selected ?? []).map(s => s.value).join(',');
                                setState(prev => ({
                                    ...prev,
                                    filterForm: { ...prev.filterForm, [field.name]: value }
                                }));
                            }}
                            placeholder="All"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    minHeight: 31,
                                    fontSize: '0.875rem',
                                    borderColor: state.isFocused ? base.borderColor : base.borderColor,
                                }),
                                valueContainer: (base) => ({
                                    ...base,
                                    paddingTop: 2,
                                    paddingBottom: 2,
                                    paddingLeft: 6,
                                }),
                                input: (base) => ({
                                    ...base,
                                    margin: 0,
                                    padding: 0,
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    height: 29,
                                }),
                                clearIndicator: (base) => ({ ...base, padding: 4 }),
                                dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                    </Form.Group>
                </Col>
            );
        }

        return (
            <Col md={field.colSize || 2} key={field.name}>
            <Form.Group>
                <Form.Label className="small">{field.label}</Form.Label>

                    {field.type === 'select' ? (
                        <Form.Select {...commonProps}>
                        <option value="">All</option>
                        {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>
                            {opt.label}
                            </option>
                        ))}
                        </Form.Select>
                    ) : (
                        <Form.Control {...commonProps} type={field.type} />
                    )}
                    </Form.Group>
            </Col>
        );
    };

    function getDateLabel(title: string) {
  switch (title) {
    case "Revenue Report":
      return "Payment Date";
    case "Redeemed Validations":
      return "Validation Date";
    default:
      return "Parked During";
  }
}
    return (
        <>
            <div className="mb-3">
                <Form onSubmit={handleSubmit}>
                    <Row className="g-2 mb-2 align-items-end">
                    {config.filterFields
                    .map(renderFilterField)}
                    </Row>
                    {config.title !== 'Aging Report' && (
                    <Row className="g-2 mb-2 align-items-end">
                    <Col xs={12} md={3}>
                        <QuickDateRangeDropdown
                        mode={quickDateMode}
                        onModeChange={setQuickDateMode}
                        label={getDateLabel(config.title)}
                        onChange={(range, mode) => {
                            setQuickDateMode(mode);
                            const newFilterForm = {
                            ...state.filterForm,
                            from: range.from,
                            to: range.to,
                            quickDateMode: mode,
                            };
                            const newFilter = {
                            ...state.filter,
                            from: range.from,
                            to: range.to,
                            };
                            setQuickDateMode(mode);
                            setState(prev => ({
                            ...prev,
                            filter: newFilter,
                            filterForm: newFilterForm,
                            }));
                        }}
                        />
                    </Col>

                    {quickDateMode === 'Custom' && (
                        <>
                        <Col xs={12} md={3}>
                            <Form.Group>
                            <Form.Label className="small form-label">From</Form.Label>
                            <Form.Control
                                type="date"
                                name="from"
                                value={state.filterForm.from}
                                onChange={handleFilterChange}
                                size="sm"
                                max={state.filterForm.to || LocalDate.now().toString()}
                            />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Group>
                            <Form.Label className="small form-label">To</Form.Label>
                            <Form.Control
                                    type="date"
                                    name="to"
                                    value={state.filterForm.to}
                                    onChange={handleFilterChange}
                                    size="sm"
                                    min={state.filterForm.from} 
                                    max={LocalDate.now().toString()}        
                            />
                            </Form.Group>
                        </Col>
                        </>
                    )}
                    </Row>
                    )}
                    {config.title === 'Revenue Report' && (
                    <Row className="g-2 mb-2 align-items-end">
                        <Col xs={12} md={3}>
                        <QuickDateRangeDropdown
                        mode={quickPayoutDateMode}
                        onModeChange={setQuickPayoutDateMode}
                        label={
                            <>
                                Payout Date{' '}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="payout-date-tooltip">
                                            The date a payment reached your bank account
                                        </Tooltip>
                                    }
                                >
                                    <InfoCircleFill 
                                        className="icon-shift-up"
                                        size={14} 
                                        style={{ cursor: 'help', marginLeft: '4px' }} 
                                    />
                                </OverlayTrigger>
                            </>
                        }
                        onChange={(range, mode) => {
                            setQuickPayoutDateMode(mode);
                            const newFilterForm = {
                            ...state.filterForm,
                            payoutFrom: range.from,
                            payoutTo: range.to,
                            quickPayoutDateMode: mode,
                            };
                            const newFilter = {
                            ...state.filter,
                            payoutFrom: range.from,
                            payoutTo: range.to,
                            };
                            setQuickPayoutDateMode(mode);
                            setState(prev => ({
                            ...prev,
                            filter: newFilter,
                            filterForm: newFilterForm,
                            }));
                        }}
                        />
                    </Col>

                    {quickPayoutDateMode === 'Custom' && (
                        <>
                        <Col xs={12} md={3}>
                            <Form.Group>
                            <Form.Label className="small form-label">From</Form.Label>
                            <Form.Control
                                type="date"
                                name="payoutFrom"
                                value={state.filterForm.payoutFrom}
                                onChange={handleFilterChange}
                                size="sm"
                                max={state.filterForm.payoutTo || LocalDate.now().toString()}
                            />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Group>
                            <Form.Label className="small form-label">To</Form.Label>
                            <Form.Control
                                    type="date"
                                    name="payoutTo"
                                    value={state.filterForm.payoutTo}
                                    onChange={handleFilterChange}
                                    size="sm"
                                    min={state.filterForm.payoutFrom} 
                                    max={LocalDate.now().toString()}        
                            />
                            </Form.Group>
                        </Col>
                        </>
                    )}
                    </Row>
                    )}
                    {config.title === 'Aging Report' && (
                        <Row className="g-2 mb-2 align-items-end">
                            <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small">Cutoff Date</Form.Label>
                                <Form.Select
                                size="sm"
                                name="cutoffMode"
                                value={state.filterForm.cutoffMode}
                                onChange={handleFilterChange}
                                >
                                <option value="All">All</option>
                                <option value="Today">Today</option>
                                <option value="LastMonth">Last Month</option>
                                <option value="Custom">Custom</option>
                                </Form.Select>
                            </Form.Group>
                            </Col>

                            {state.filterForm.cutoffMode === 'Custom' && (
                            <Col md={3}>
                                <Form.Group>
                                <Form.Label className="small">As of</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    name="cutoffDate"
                                    value={state.filterForm.cutoffDate}
                                    max={LocalDate.now().toString()}
                                    onChange={handleFilterChange}
                                />
                                </Form.Group>
                            </Col>
                            )}
                        </Row>
                        )}
                    <Row className="align-items-end mt-2">
                    <Col xs={12} md={8}>
                        <div className="d-flex gap-2 mt-2">
                        <div className="position-relative">
                            <Button variant="primary" type="submit" size="sm">
                            <PlayFill className="me-1 icon-shift-up" />
                            Run
                            </Button>
                            {state.showArrow && (
                            <BounceArrow
                                className="mx-7 mt-5"
                                direction="up"
                                position="left"
                                message="Fill in the filters and click here to run the report!"
                            />
                            )}
                        </div>
                        <Button variant="secondary" onClick={handleReset} size="sm">
                            <X className="me-1 icon-shift-up" />
                            Clear
                        </Button>
                        </div>
                    </Col>

                    <Col xs={12} md={4} className="d-flex justify-content-end gap-2 mt-2 mt-md-0">
                        {state.showGrid && (
                        !canSaveReport ? (
                            <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="save-report-tooltip">
                                Fill out at least one filter to save the report
                                </Tooltip>
                            }
                            >
                            <span>
                                <Button
                                variant="primary"
                                size="sm"
                                disabled={!canSaveReport}
                                onClick={() => setShowSaveModal(true)}
                                >
                                <FloppyFill className="me-2 icon-shift-up" />
                                Save Report Criteria
                                </Button>
                            </span>
                            </OverlayTrigger>
                        ) : (
                            <Button
                            variant="primary"
                            size="sm"
                            disabled={!canSaveReport}
                            onClick={() => setShowSaveModal(true)}
                            >
                            <FloppyFill className="me-2 icon-shift-up" />
                            Save Report Criteria
                            </Button>
                        )
                        )}
                        <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowManageModal(true)}
                        >
                        Saved Reports
                        </Button>

                        {config.defaultColumns.some(col => col.hide !== false) && state.showGrid && (
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-columns-gap me-1"></i>
                            Columns
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ padding: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                            <div className="mb-2 border-bottom pb-2" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                <Form.Check
                                type="checkbox"
                                id={`select-all-${config.id}`}
                                label="Select All"
                                checked={Object.values(state.columnVisibility).every(v => v)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    toggleAllColumns(e.target.checked);
                                }}
                                />
                            </div>
                            {config.defaultColumns.map(col => (
                                <div
                                key={col.field}
                                className="mb-1"
                                onClick={(e) => e.stopPropagation()}
                                >
                                <Form.Check
                                    type="checkbox"
                                    id={`col-${col.field}`}
                                    label={col.headerName}
                                    checked={state.columnVisibility[col.field as string] ?? true}
                                    onChange={(e) => {
                                    e.stopPropagation();
                                    toggleColumn(col.field as string, e.target.checked);
                                    }}
                                    disabled={col.hide === false}
                                />
                                </div>
                            ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        )}
                    </Col>
                    </Row>
                </Form>
            </div>
            <div style={{ width: '100%', height: `calc(100vh - ${350 + heightOffset}px)`, minHeight: '300px', overflow: 'auto' }}>
                {!state.showGrid ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100" style={{ padding: '20px', overflow: 'auto' }}>
                        <div className="card shadow-sm" style={{ width: '100%', maxWidth: '800px', margin: 'auto' }}>
                            <div className="card-body p-5">
                                <h4 className="text-primary mb-3 fw-bold opacity-75 text-center">
                                    {config.icon} {config.title}
                                </h4>
                                <div 
                                    className="text-muted"
                                    dangerouslySetInnerHTML={{ __html: config.description }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <CSSTransition
                        in={state.showGrid}
                        timeout={300}
                        classNames="grid-slide"
                        unmountOnExit
                    >
                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: 1, overflow: 'auto' }}>
                                <DataGrid
                                    rowData={state.data}
                                    columnDefs={state.columnDefs}
                                    onGridReady={onGridReady}
                                    onRowClicked={onRowClicked}
                                    rowStyle={{ cursor: 'pointer' }}
                                    pagination={true}
                                    paginationPageSize={state.filter.pageSize}
                                    paginationPageSizeSelector={[10, 25, 50, 100]}
                                    paginationAutoPageSize={false}
                                    suppressPaginationPanel={false}
                                    suppressScrollOnNewData={true}
                                    exportAllColumns={true}
                                    onPaginationChanged={(params) => {
                                        const pageSize = params.api.paginationGetPageSize();
                                        if (pageSize !== state.filter.pageSize) {
                                            setState(prev => ({
                                                ...prev,
                                                filter: { ...prev.filter, pageSize } as TFilter
                                            }));
                                        }
                                    }}
                                    domLayout="normal"
                                    defaultColDef={{
                                        sortable: true,
                                        filter: true,
                                        resizable: true
                                    }}
                                />
                            </div>
                            <GridFooter 
                                type={config.id as any} 
                                data={state.data} 
                                columnDefs={state.columnDefs} 
                            />
                        </div>
                    </CSSTransition>
                )}
            </div>
            <SaveReportModal
            show={showSaveModal}
            onClose={() => setShowSaveModal(false)}
            onSave={handleSaveReport}
            reportName={saveReportName}
            setReportName={setSaveReportName}
            />
            <ManageSavedReportsModal
            show={showManageModal}
            onClose={() => setShowManageModal(false)}
            savedReports={savedReports}
            setSavedReports={setSavedReports}
            onRun={(filters) => handleSubmit(null, filters)}
            currentFacilityIds={facilityIds}
            facilities={config.facilities}
            reportType={config.title}
            valueLabelMaps={valueLabelMaps}
            />
        </>
    );
} 

export type QuickDateOption =
  | 'All'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'This Month'
  | 'Last Month'
  | 'This Year'
  | 'Custom';